import type { ReactNode } from 'react'
import { formatNumber } from '@/lib/format'

export interface BarDatum {
  readonly label: string
  readonly value: number
  /** Optionaler Zusatz rechts, etwa die Anzahl dahinter. */
  readonly secondaryLabel?: string
}

interface BarListProps {
  readonly data: readonly BarDatum[]
  readonly emptyMessage?: string
}

/**
 * Waagerechtes Balkendiagramm für Größenvergleiche.
 *
 * Waagerecht, weil die Beschriftungen Wortmarken sind: Gedrehte oder
 * abgeschnittene Achsenbeschriftungen sind der häufigste Grund, warum ein
 * Diagramm unlesbar wird.
 *
 * Die Werte stehen direkt am Balken statt an einer Achse. Bei einer
 * Rangliste ist die genaue Zahl wichtiger als das Ablesen an einer Skala,
 * und eine Achse weniger ist eine Zeile weniger Rauschen.
 */
export function BarList({ data, emptyMessage = 'Keine Daten' }: BarListProps): ReactNode {
  if (data.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">{emptyMessage}</p>
    )
  }

  const max = Math.max(...data.map((item) => item.value), 1)

  return (
    <ul className="flex flex-col gap-2.5">
      {data.map((item) => {
        const widthPercent = Math.max((item.value / max) * 100, 1.5)
        return (
          <li key={item.label} className="group">
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="truncate text-slate-700 dark:text-slate-200" title={item.label}>
                {item.label}
              </span>
              <span className="shrink-0 tabular-nums font-medium text-slate-900 dark:text-slate-100">
                {formatNumber(item.value)}
                {item.secondaryLabel !== undefined && (
                  <span className="ml-1.5 font-normal text-slate-400 dark:text-slate-500">
                    {item.secondaryLabel}
                  </span>
                )}
              </span>
            </div>
            {/* Dünner Balken, an der Grundlinie verankert, Ende gerundet. */}
            <div className="mt-1 h-2 w-full overflow-hidden rounded-sm bg-slate-100 dark:bg-slate-700">
              <div
                className="h-full rounded-r-[4px] transition-[width] duration-300"
                style={{
                  width: `${String(widthPercent)}%`,
                  backgroundColor: 'var(--viz-series-1)',
                }}
              />
            </div>
          </li>
        )
      })}
    </ul>
  )
}
